import { useEffect, useRef, useState } from 'react';
import Avatar from './Avatar';
import { PhotoError, uploadProfilePhoto } from '../data/photoService';
import './PhotoPicker.css';

/**
 * Avatar + "Change photo". Picking a file uploads it straight away and saves the
 * new photoUrl on the profile (no need to press "Save profile").
 *
 *   <PhotoPicker uid={uid} user={user} onChange={(url) => ...} />
 */
export default function PhotoPicker({ uid, user, size = 72, onChange }) {
  const inputRef = useRef(null);
  const previewRef = useRef(null);
  const [preview, setPreview] = useState(null); // local object URL while/after uploading
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Free the local preview URL when it's replaced or on unmount.
  useEffect(() => () => previewRef.current && URL.revokeObjectURL(previewRef.current), []);
  const setLocalPreview = (url) => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = url;
    setPreview(url);
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // so picking the same file again still fires
    if (!file) return;

    setError('');
    setDone(false);
    setBusy(true);
    setLocalPreview(URL.createObjectURL(file));
    try {
      const url = await uploadProfilePhoto(uid, file);
      setDone(true);
      if (onChange) onChange(url);
    } catch (err) {
      if (!(err instanceof PhotoError)) console.error('Photo upload failed', err);
      setLocalPreview(null);
      setError(err instanceof PhotoError ? err.message : 'Upload failed. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const shown = preview ? { ...user, photoUrl: preview } : user;

  return (
    <div className="photo-picker">
      <div className={`photo-picker__img${busy ? ' photo-picker__img--busy' : ''}`}>
        <Avatar user={shown} size={size} decorative={false} />
        {busy && <span className="photo-picker__spinner" aria-hidden="true" />}
      </div>
      <div className="photo-picker__side">
        <button type="button" className="btn btn--ghost" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : 'Change photo'}
        </button>
        <span className="photo-picker__hint">JPG, PNG or WebP, up to 10 MB. We’ll crop it to a square.</span>
        <span role="status" className="photo-picker__done">{done ? 'Photo updated.' : ''}</span>
        {error && <span role="alert" className="photo-picker__error">{error}</span>}
      </div>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} />
    </div>
  );
}
